import { Alert, Button, Card, Space, Table, Tag, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { agentBindStart, agentListDevices, agentRevokeDevice } from '../api';
import { resolveErrorMessage } from '../utils/errorMessage';

const AgentManagement = () => {
  const [devices, setDevices] = useState([]);
  const [bindInfo, setBindInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bindLoading, setBindLoading] = useState(false);
  const [error, setError] = useState('');

  const loadDevices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await agentListDevices();
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '获取设备失败'));
      }
      setDevices(resp.devices || []);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDevices().catch((e) => setError(e?.message || String(e)));
  }, [loadDevices]);

  const handleBindStart = async () => {
    setBindLoading(true);
    setError('');
    try {
      const resp = await agentBindStart();
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '生成绑定码失败'));
      }
      setBindInfo({ token: resp.bind_token, expires_at: resp.expires_at });
      message.success('绑定码已生成');
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBindLoading(false);
    }
  };

  const handleRevoke = async (deviceId) => {
    setLoading(true);
    setError('');
    try {
      const resp = await agentRevokeDevice(deviceId);
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.error_message, '解绑失败'));
      }
      message.success('设备已解绑');
      await loadDevices();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    {
      title: '设备名称',
      dataIndex: 'device_name',
      render: (value, record) => value || record.device_id || '-',
    },
    {
      title: '平台',
      dataIndex: 'platforms',
      render: (value) => (Array.isArray(value) ? value : [])
        .filter((item) => item)
        .map((item) => <Tag key={item}>{item}</Tag>),
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (value) => (
        <Tag color={String(value || '').toLowerCase() === 'active' ? 'green' : 'default'}>
          {value || '-'}
        </Tag>
      ),
    },
    {
      title: '最近在线',
      dataIndex: 'last_seen_at',
      render: (value) => value || '-',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Button
          danger
          size="small"
          onClick={() => handleRevoke(record.device_id)}
          disabled={String(record.status || '').toLowerCase() !== 'active'}
        >
          解绑
        </Button>
      ),
    },
  ]), [handleRevoke]);

  return (
    <div className="page-shell">
      <Card className="page-card" bodyStyle={{ padding: 0 }}>
        <div className="page-panel">
          <div className="page-panel__top" style={{ padding: '16px 18px' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                绑定本地 Agent
              </Typography.Title>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                生成绑定码后，在 Agent 客户端输入即可绑定设备。每个账号最多绑定 1 台设备。
              </Typography.Paragraph>
              <Space>
                <Button type="primary" loading={bindLoading} onClick={handleBindStart}>
                  生成绑定码
                </Button>
                <Button onClick={loadDevices}>刷新设备列表</Button>
              </Space>
              {bindInfo && (
                <Alert
                  type="info"
                  showIcon
                  message="绑定码"
                  description={(
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Typography.Text copyable>{bindInfo.token}</Typography.Text>
                      <Typography.Text type="secondary">有效期至：{bindInfo.expires_at || '-'}</Typography.Text>
                    </div>
                  )}
                />
              )}
              {error && <Alert type="error" showIcon message="提示" description={error} />}
            </Space>
          </div>

          <div className="page-panel__body" style={{ padding: '0 12px 12px' }}>
            <Table
              rowKey="device_id"
              columns={columns}
              dataSource={devices}
              loading={loading}
              pagination={false}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AgentManagement;
